import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ExtensionReassignmentsPanel from './ExtensionReassignmentsPanel';
import {
  Activity, ChevronDown, ChevronUp, Database, Loader2, Play,
  RefreshCw, Settings, Zap, Clock, Plus, Trash2, RotateCcw, AlertTriangle, Repeat,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
};

/**
 * System Health Card — master-admin only.
 *
 * Three related admin controls in one collapsible card so the Advanced tab
 * doesn't grow another top-level section:
 *   1. **Run Backfill** — one-click bulk backfill for caller_intel +
 *      deal_outcomes on a chosen domain (or all). The startup-boot job
 *      is capped at 30 items so a giant backlog needs this button.
 *   2. **Auto-reassign threshold** — enable + tune the auto-suggest flag
 *      that red-stars revivable-queue rows where the original agent has
 *      a poor chase rate and a "proven chaser" is available.
 *   3. **Slow-request log** — most recent /api/* calls that crossed
 *      their timing threshold. Zero external deps; middleware persists
 *      the entries to `db.slow_requests`.
 */
const SystemHealthCard = ({ domainId }) => {
  const [expanded, setExpanded] = useState(false);
  const [running, setRunning] = useState(false);
  const [slow, setSlow] = useState([]);
  const [loadingSlow, setLoadingSlow] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [curve, setCurve] = useState(null);
  const [savingCurve, setSavingCurve] = useState(false);
  const [inflight, setInflight] = useState(null);
  const [staleRetry, setStaleRetry] = useState(null);
  const [staleRetryLoading, setStaleRetryLoading] = useState(false);

  const loadSlow = useCallback(async () => {
    setLoadingSlow(true);
    try {
      const r = await axios.get(`${API}/admin/perf/slow-requests?limit=25`);
      setSlow(r.data?.rows || []);
    } catch { /* silent */ } finally { setLoadingSlow(false); }
  }, []);

  const loadCfg = useCallback(async () => {
    if (!domainId) return;
    try {
      const r = await axios.get(`${API}/admin/domains/${domainId}/auto-reassign-config`);
      setCfg(r.data);
    } catch { /* silent */ }
  }, [domainId]);

  const loadCurve = useCallback(async () => {
    if (!domainId) return;
    try {
      const r = await axios.get(`${API}/admin/domains/${domainId}/revival-recency-curve`);
      setCurve(r.data);
    } catch { /* silent */ }
  }, [domainId]);

  const loadInflight = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/runtime/inflight`);
      setInflight(r.data);
    } catch { /* silent — endpoint is master-admin-only */ }
  }, []);

  const loadStaleRetry = useCallback(async () => {
    setStaleRetryLoading(true);
    try {
      const r = await axios.get(`${API}/admin/calls/stale-retry?days=365`);
      setStaleRetry(r.data);
    } catch { /* silent — master-admin-only */ } finally {
      setStaleRetryLoading(false);
    }
  }, []);

  const runStaleRetryNow = async () => {
    setStaleRetryLoading(true);
    try {
      await axios.post(`${API}/admin/calls/stale-retry/run-now`);
      await loadStaleRetry();
      toast.success('Stale-retry sweeper ran — counts refreshed.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sweeper failed.');
    } finally {
      setStaleRetryLoading(false);
    }
  };

  const retrySingleCall = async (callId) => {
    // Fire-and-refresh: kick the call back into pipeline via the existing
    // single-call retry endpoint. Optimistically remove from the preview
    // so the admin sees the row disappear immediately.
    try {
      await axios.post(`${API}/admin/calls/${encodeURIComponent(callId)}/retry-transcript`);
      setStaleRetry((prev) => prev ? ({
        ...prev,
        preview: (prev.preview || []).filter((r) => r.call_id !== callId),
        total: Math.max(0, (prev.total || 0) - 1),
      }) : prev);
      toast.success('Call re-queued for transcription + grading.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed.');
    }
  };

  const reclassifyInternalNow = async () => {
    // Runs the internal-to-internal backfill for the current tenant
    // (or every tenant when the master admin views the health card
    // without a domain scope). Removes any polluted gradings so
    // governance drill-downs drop the misclassified calls immediately.
    if (!window.confirm(
      'Scan calls for both-endpoint-monitored (internal-to-internal), stamp them as internal, ' +
      'and delete their gradings so governance drill-downs are accurate. Continue?'
    )) return;
    try {
      const url = domainId
        ? `${API}/admin/calls/reclassify-internal?domain_id=${encodeURIComponent(domainId)}`
        : `${API}/admin/calls/reclassify-internal`;
      const r = await axios.post(url);
      const d = r.data || {};
      toast.success(
        `Reclassified ${d.total_flagged || 0} call${d.total_flagged === 1 ? '' : 's'}` +
        ` · removed ${d.total_gradings_removed || 0} polluted grading${d.total_gradings_removed === 1 ? '' : 's'}.`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reclassify failed.');
    }
  };

  useEffect(() => {
    if (!expanded) return;
    loadSlow();
    loadCfg();
    loadCurve();
    loadInflight();
    loadStaleRetry();
    // Auto-poll inflight every 10s while the card is expanded so the
    // panel actually reflects live state during a "sits and spins"
    // incident. Cleared on collapse to avoid background traffic when
    // nobody's watching.
    const t = setInterval(loadInflight, 10000);
    return () => clearInterval(t);
  }, [expanded, loadSlow, loadCfg, loadCurve, loadInflight, loadStaleRetry]);

  const runBackfill = async (days = 30) => {
    setRunning(true);
    try {
      const p = new URLSearchParams();
      if (domainId) p.set('domain_id', domainId);
      p.set('days', String(days));
      const r = await axios.post(`${API}/admin/backfill/run?${p.toString()}`);
      const est = r.data?.pending_estimate ?? 0;
      if (r.data?.started) {
        toast.success(
          est > 0
            ? `Backfill started (${days}d window) — ${est} calls to classify in the background.`
            : `Backfill triggered (${days}d window) — nothing pending, no work to do.`
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed to start.');
    } finally { setRunning(false); }
  };

  const clearSlow = async () => {
    try {
      await axios.delete(`${API}/admin/perf/slow-requests`);
      setSlow([]);
      toast.success('Slow-request log cleared.');
    } catch { toast.error('Could not clear log.'); }
  };

  const saveCfg = async (patch) => {
    if (!domainId) return;
    setSavingCfg(true);
    try {
      const merged = { ...cfg, ...patch };
      const r = await axios.put(`${API}/admin/domains/${domainId}/auto-reassign-config`, merged);
      setCfg(r.data);
      toast.success('Auto-reassign config saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save config.');
    } finally { setSavingCfg(false); }
  };

  const saveCurve = async (next) => {
    if (!domainId) return;
    setSavingCurve(true);
    try {
      const r = await axios.put(
        `${API}/admin/domains/${domainId}/revival-recency-curve`,
        { breakpoints: next.breakpoints, tail_multiplier: next.tail_multiplier },
      );
      setCurve({ ...r.data, is_default: false });
      toast.success('Recency curve updated.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save curve.');
    } finally { setSavingCurve(false); }
  };

  const resetCurve = async () => {
    if (!domainId) return;
    setSavingCurve(true);
    try {
      await axios.put(`${API}/admin/domains/${domainId}/revival-recency-curve`, { reset: true });
      await loadCurve();
      toast.success('Recency curve restored to defaults.');
    } catch {
      toast.error('Could not reset curve.');
    } finally { setSavingCurve(false); }
  };

  const updateBreakpoint = (idx, patch) => {
    const bps = curve.breakpoints.map((b, i) => i === idx ? { ...b, ...patch } : b);
    setCurve({ ...curve, breakpoints: bps });
  };
  const addBreakpoint = () => {
    const last = curve.breakpoints[curve.breakpoints.length - 1];
    const next = { max_age_days: (last?.max_age_days || 14) + 7, multiplier: 0.3 };
    setCurve({ ...curve, breakpoints: [...curve.breakpoints, next] });
  };
  const removeBreakpoint = (idx) => {
    setCurve({ ...curve, breakpoints: curve.breakpoints.filter((_, i) => i !== idx) });
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="system-health-card">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 rounded-t-xl"
        data-testid="system-health-toggle"
      >
        <Activity className="w-4 h-4 text-rose-600" />
        <div className="text-left">
          <p className="text-sm font-bold text-slate-900">System Health & Admin Controls</p>
          <p className="text-[11px] text-slate-500">
            Manual backfill · Auto-reassign threshold · Slow request log
          </p>
        </div>
        {expanded ? (
          <ChevronUp className="ml-auto w-4 h-4 text-slate-500" />
        ) : (
          <ChevronDown className="ml-auto w-4 h-4 text-slate-500" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-5 border-t border-slate-200 pt-3">

          {/* -- Manual backfill -- */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Database className="w-4 h-4 text-indigo-600" />
              <p className="text-xs font-bold uppercase tracking-wider text-slate-700">Run Backfill</p>
            </div>
            <p className="text-xs text-slate-600 mb-2 leading-snug">
              The boot-time backfill caps at 30 items so a huge backlog can
              take multiple restarts to catch up. Use these to process the
              full pending queue in one shot (runs in the background). New
              calls are now classified inline at grade-time — this button is
              only needed to catch up on historical gaps.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => runBackfill(30)}
                disabled={running}
                className="text-xs font-semibold px-3 py-1.5 rounded-md bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-white inline-flex items-center gap-1.5"
                data-testid="system-health-run-backfill"
              >
                {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                Quick backfill (30 days)
              </button>
              <button
                onClick={() => runBackfill(365)}
                disabled={running}
                className="text-xs font-semibold px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white inline-flex items-center gap-1.5"
                data-testid="system-health-run-deep-backfill"
                title="Use this if the Revivable Queue looks stale by more than a month (e.g. after an LLM budget outage)."
              >
                {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                Deep backfill (365 days)
              </button>
              <button
                onClick={async () => {
                  if (!domainId) return;
                  setRunning(true);
                  try {
                    const r = await axios.post(
                      `${API}/admin/domains/${domainId}/resync-customer-phones?days_back=90`,
                    );
                    const upd = r.data?.updated ?? 0;
                    const cands = r.data?.candidates_in_db ?? 0;
                    toast.success(
                      cands === 0
                        ? 'No blank customer phones in the last 90 days — nothing to re-sync.'
                        : `Re-sync complete — repopulated ${upd} of ${cands} blank customer phones.`
                    );
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Re-sync failed.');
                  } finally {
                    setRunning(false);
                  }
                }}
                disabled={running}
                className="text-xs font-semibold px-3 py-1.5 rounded-md bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60 text-white inline-flex items-center gap-1.5"
                data-testid="system-health-resync-phones"
                title="Re-fetch CDRs from Yabbit to repopulate customer_phone on outbound rows blanked by the 2026-07-23 migration."
              >
                {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Re-sync customer phones
              </button>
            </div>
          </section>

          {/* -- Auto-reassign config -- */}
          {cfg && (
            <section className="border-t border-slate-100 pt-4">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-amber-600" />
                <p className="text-xs font-bold uppercase tracking-wider text-slate-700">Auto-Reassign Threshold</p>
              </div>
              <p className="text-xs text-slate-600 mb-3 leading-snug">
                When enabled, revivable-queue rows get a red-star badge if the
                original agent's chase rate on the caller's DISC is below the
                threshold AND a "proven chaser" is available.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={cfg.enabled}
                    onChange={(e) => saveCfg({ enabled: e.target.checked })}
                    disabled={savingCfg}
                    className="rounded"
                    data-testid="system-health-auto-reassign-enabled"
                  />
                  Enabled
                </label>
                <label className="text-xs text-slate-700">
                  <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">
                    Min chase boost (1-15)
                  </span>
                  <input
                    type="number" min={1} max={15}
                    value={cfg.min_chase_boost}
                    onChange={(e) => setCfg({ ...cfg, min_chase_boost: Number(e.target.value) })}
                    onBlur={() => saveCfg({ min_chase_boost: cfg.min_chase_boost })}
                    disabled={savingCfg}
                    className="w-24 px-2 py-1 rounded border border-slate-200 text-sm tabular-nums"
                    data-testid="system-health-min-chase-boost"
                  />
                </label>
                <label className="text-xs text-slate-700">
                  <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">
                    Original chase rate below (%)
                  </span>
                  <input
                    type="number" min={0} max={100}
                    value={Math.round((cfg.original_chase_rate_below || 0) * 100)}
                    onChange={(e) => setCfg({ ...cfg, original_chase_rate_below: Number(e.target.value) / 100 })}
                    onBlur={() => saveCfg({ original_chase_rate_below: cfg.original_chase_rate_below })}
                    disabled={savingCfg}
                    className="w-24 px-2 py-1 rounded border border-slate-200 text-sm tabular-nums"
                    data-testid="system-health-orig-chase-rate"
                  />
                </label>
              </div>
            </section>
          )}

          {/* -- Revival recency curve -- */}
          {curve && (
            <section className="border-t border-slate-100 pt-4" data-testid="system-health-recency-curve">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-cyan-600" />
                <p className="text-xs font-bold uppercase tracking-wider text-slate-700">Revivable-Queue Recency Curve</p>
                {curve.is_default
                  ? <span className="text-[10px] text-slate-400 italic">(defaults)</span>
                  : <span className="text-[10px] text-cyan-700 font-semibold">(custom)</span>}
                {!curve.is_default && (
                  <button
                    onClick={resetCurve}
                    disabled={savingCurve}
                    className="ml-auto text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                    data-testid="system-health-curve-reset"
                    title="Restore the default curve"
                  >
                    <RotateCcw className="w-3 h-3" /> Reset
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-600 mb-3 leading-snug">
                When the queue is sorted by <em>Highest confidence</em>, each row's
                raw confidence gets multiplied by the multiplier that matches its
                age. Fresh deals bubble up; stale deals fade. Managers who chase
                harder can flatten this (e.g. keep 14-day-old deals at 0.8×);
                teams with a shorter attention span can drop the tail to 0.2×.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
                      <th className="py-1.5 pr-3">Up to age (days)</th>
                      <th className="py-1.5 pr-3">Confidence ×</th>
                      <th className="py-1.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {curve.breakpoints.map((bp, i) => (
                      <tr key={i} data-testid={`curve-row-${i}`}>
                        <td className="py-1 pr-3">
                          <input
                            type="number" min={1} max={365}
                            value={bp.max_age_days}
                            onChange={(e) => updateBreakpoint(i, { max_age_days: Number(e.target.value) })}
                            className="w-24 px-2 py-1 rounded border border-slate-200 text-sm tabular-nums"
                            data-testid={`curve-age-${i}`}
                          />
                        </td>
                        <td className="py-1 pr-3">
                          <input
                            type="number" min={0} max={3} step={0.05}
                            value={bp.multiplier}
                            onChange={(e) => updateBreakpoint(i, { multiplier: Number(e.target.value) })}
                            className="w-24 px-2 py-1 rounded border border-slate-200 text-sm tabular-nums"
                            data-testid={`curve-mult-${i}`}
                          />
                        </td>
                        <td className="py-1">
                          <button
                            onClick={() => removeBreakpoint(i)}
                            className="text-red-500 hover:text-red-700 p-1"
                            data-testid={`curve-remove-${i}`}
                            title="Remove this breakpoint"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t border-slate-100 text-slate-500">
                      <td className="py-1.5 pr-3 italic">Everything older</td>
                      <td className="py-1 pr-3">
                        <input
                          type="number" min={0} max={3} step={0.05}
                          value={curve.tail_multiplier}
                          onChange={(e) => setCurve({ ...curve, tail_multiplier: Number(e.target.value) })}
                          className="w-24 px-2 py-1 rounded border border-slate-200 text-sm tabular-nums"
                          data-testid="curve-tail"
                        />
                      </td>
                      <td className="py-1"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={addBreakpoint}
                  disabled={savingCurve}
                  className="text-xs font-semibold px-3 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1.5"
                  data-testid="curve-add"
                >
                  <Plus className="w-3 h-3" /> Add breakpoint
                </button>
                <button
                  onClick={() => saveCurve(curve)}
                  disabled={savingCurve}
                  className="text-xs font-semibold px-3 py-1.5 rounded-md bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60 text-white inline-flex items-center gap-1.5"
                  data-testid="curve-save"
                >
                  {savingCurve && <Loader2 className="w-3 h-3 animate-spin" />}
                  Save curve
                </button>
              </div>
            </section>
          )}

          {/* -- Live pipeline (inflight snapshot) -- */}
          {inflight && (
            <section className="border-t border-slate-100 pt-4" data-testid="system-health-inflight">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-emerald-600" />
                <p className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Live Pipeline Snapshot
                </p>
                <span className="text-[10px] text-slate-400 italic">refreshes every 10s</span>
                <button
                  onClick={loadInflight}
                  className="ml-auto text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                  data-testid="system-health-inflight-refresh"
                >
                  <RefreshCw className="w-3 h-3" /> Refresh
                </button>
              </div>
              <p className="text-xs text-slate-600 mb-3 leading-snug">
                First place to check if the app feels sluggish. A locked
                classify semaphore means an LLM burst is draining slowly;
                a stuck per-domain sync lock means a previous 5-min tick
                is still running.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                  <p className="text-[10px] uppercase text-slate-500 tracking-wider">Asyncio tasks</p>
                  <p className="text-lg font-bold text-slate-800 tabular-nums" data-testid="inflight-tasks">
                    {inflight.asyncio_tasks_total ?? '—'}
                  </p>
                </div>
                <div className={`rounded-lg border p-3 ${
                  inflight.grading_semaphore_locked
                    ? 'bg-amber-50 border-amber-300'
                    : 'bg-slate-50 border-slate-200'
                }`}>
                  <p className="text-[10px] uppercase text-slate-500 tracking-wider">Grading sem.</p>
                  <p className="text-lg font-bold tabular-nums" data-testid="inflight-grading">
                    {inflight.grading_semaphore_locked
                      ? <span className="text-amber-700">Locked</span>
                      : <span className="text-emerald-700">Idle</span>}
                  </p>
                </div>
                <div className={`rounded-lg border p-3 ${
                  inflight.classify_semaphore_locked
                    ? 'bg-amber-50 border-amber-300'
                    : 'bg-slate-50 border-slate-200'
                }`}>
                  <p className="text-[10px] uppercase text-slate-500 tracking-wider">Classify sem.</p>
                  <p className="text-lg font-bold tabular-nums" data-testid="inflight-classify">
                    {inflight.classify_semaphore_locked
                      ? <span className="text-amber-700">Locked</span>
                      : <span className="text-emerald-700">Idle</span>}
                  </p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                  <p className="text-[10px] uppercase text-slate-500 tracking-wider">Domain locks</p>
                  <p className="text-lg font-bold text-slate-800 tabular-nums" data-testid="inflight-locks">
                    {Object.values(inflight.domain_sync_locks || {}).filter(l => l?.locked).length}
                    <span className="text-xs font-normal text-slate-400"> / {Object.keys(inflight.domain_sync_locks || {}).length}</span>
                  </p>
                </div>
              </div>
              {Object.keys(inflight.domain_sync_locks || {}).length > 0 && (
                <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 mb-3">
                  <p className="text-[10px] uppercase text-slate-500 tracking-wider mb-1.5">Per-domain sync locks</p>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(inflight.domain_sync_locks).map(([did, info]) => (
                      <span
                        key={did}
                        className={`text-[11px] font-mono px-2 py-0.5 rounded ${
                          info?.locked ? 'bg-amber-100 text-amber-800' : 'bg-emerald-50 text-emerald-800'
                        }`}
                        data-testid={`inflight-lock-${did.slice(0, 8)}`}
                        title={did}
                      >
                        {did.slice(0, 8)} {info?.locked ? '🔒' : '✓'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {inflight.asyncio_tasks_by_bucket && Object.keys(inflight.asyncio_tasks_by_bucket).length > 0 && (
                <details className="text-[11px] text-slate-500">
                  <summary className="cursor-pointer hover:text-slate-800">Task buckets</summary>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {Object.entries(inflight.asyncio_tasks_by_bucket).map(([k, n]) => (
                      <span key={k} className="font-mono bg-slate-100 px-2 py-0.5 rounded">
                        {k}: <span className="tabular-nums font-semibold text-slate-700">{n}</span>
                      </span>
                    ))}
                  </div>
                </details>
              )}
            </section>
          )}

          {/* -- Stale-retry sweeper -- */}
          <section className="border-t border-slate-100 pt-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
              <p className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Stale Retry Calls (parked &gt; 72h)
              </p>
              <button
                onClick={loadStaleRetry}
                className="ml-auto text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                data-testid="stale-retry-refresh"
              >
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
              <button
                onClick={runStaleRetryNow}
                disabled={staleRetryLoading}
                className="text-[11px] font-semibold text-white bg-rose-600 hover:bg-rose-500 disabled:opacity-60 px-2 py-1 rounded inline-flex items-center gap-1"
                data-testid="stale-retry-run-now"
                title="Immediately re-scan for calls stuck > 72h and update the flag counts."
              >
                {staleRetryLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                Run sweeper now
              </button>
              <button
                onClick={reclassifyInternalNow}
                className="text-[11px] font-semibold text-white bg-indigo-600 hover:bg-indigo-500 px-2 py-1 rounded inline-flex items-center gap-1"
                data-testid="reclassify-internal-run-now"
                title="Scan for calls where BOTH endpoints are on the monitored-extensions list (staff huddles / transfers), mark them internal, and delete their polluted gradings."
              >
                <RotateCcw className="w-3 h-3" />
                Reclassify internal{domainId ? ' (this tenant)' : ' (all tenants)'}
              </button>
            </div>
            <p className="text-xs text-slate-600 mb-3 leading-snug">
              Calls stuck in <code className="text-[11px] bg-slate-100 px-1 rounded">no_transcript_pending_retry</code>{' '}
              for more than 72h. Nightly sweeper flags them at 2:30pm AEST so
              they can be retried via the tenant&apos;s &ldquo;Retry no-transcript&rdquo;
              button (or written off).
            </p>
            {staleRetryLoading && !staleRetry ? (
              <div className="text-xs text-slate-500 inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading…
              </div>
            ) : staleRetry ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                  <div className={`rounded-lg border p-3 ${
                    staleRetry.total > 0
                      ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'
                  }`}>
                    <p className="text-[10px] uppercase text-slate-500 tracking-wider">Total flagged</p>
                    <p className={`text-2xl font-bold tabular-nums ${
                      staleRetry.total > 0 ? 'text-rose-700' : 'text-emerald-700'
                    }`} data-testid="stale-retry-total">
                      {staleRetry.total}
                    </p>
                  </div>
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <p className="text-[10px] uppercase text-slate-500 tracking-wider">Last swept</p>
                    <p className="text-sm font-semibold text-slate-800" data-testid="stale-retry-last-run">
                      {_fmtDate(staleRetry.last_run?.finished || staleRetry.last_run?.started)}
                    </p>
                  </div>
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <p className="text-[10px] uppercase text-slate-500 tracking-wider">Newly flagged</p>
                    <p className="text-sm font-semibold text-slate-800 tabular-nums">
                      {staleRetry.last_run?.summary?.modified ?? 0}
                    </p>
                  </div>
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <p className="text-[10px] uppercase text-slate-500 tracking-wider">Age threshold</p>
                    <p className="text-sm font-semibold text-slate-800 tabular-nums">
                      {staleRetry.last_run?.summary?.age_hours_threshold ?? 72}h
                    </p>
                  </div>
                </div>
                {(staleRetry.per_domain || []).length > 0 && (
                  <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 mb-3">
                    <p className="text-[10px] uppercase text-slate-500 tracking-wider mb-1.5">By tenant</p>
                    <div className="flex flex-wrap gap-1.5">
                      {staleRetry.per_domain.map((d) => (
                        <span
                          key={d.domain_id || 'no-domain'}
                          className={`text-[11px] font-mono px-2 py-0.5 rounded ${
                            d.count > 50 ? 'bg-rose-100 text-rose-800' :
                            d.count > 10 ? 'bg-amber-100 text-amber-800' :
                            'bg-slate-100 text-slate-700'
                          }`}
                          title={d.domain_id || 'unattributed'}
                          data-testid={`stale-retry-tenant-${(d.yabbit_domain || 'unknown').replace(/\s+/g, '-')}`}
                        >
                          {d.yabbit_domain || 'unattributed'}: {d.count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {(staleRetry.preview || []).length > 0 && (
                  <details className="text-[11px] text-slate-500">
                    <summary className="cursor-pointer hover:text-slate-800 font-semibold">
                      Oldest {staleRetry.preview.length} preview
                    </summary>
                    <div className="mt-2 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                          <tr>
                            <th className="py-1 pr-2 font-semibold">Tenant</th>
                            <th className="py-1 pr-2 font-semibold">Agent</th>
                            <th className="py-1 pr-2 font-semibold">Call date</th>
                            <th className="py-1 pr-2 font-semibold">Duration</th>
                            <th className="py-1 pr-2 font-semibold">Flagged at</th>
                            <th className="py-1 pr-2 font-semibold text-right">Retry</th>
                          </tr>
                        </thead>
                        <tbody>
                          {staleRetry.preview.map((r) => (
                            <tr key={r.call_id} className="border-b border-slate-100">
                              <td className="py-1 pr-2 text-slate-700 font-mono text-[11px]">
                                {r.yabbit_domain || '—'}
                              </td>
                              <td className="py-1 pr-2 text-slate-700">
                                {r.agent_name || r.agent_id || '—'}
                              </td>
                              <td className="py-1 pr-2 tabular-nums text-slate-600">{_fmtDate(r.call_date)}</td>
                              <td className="py-1 pr-2 tabular-nums text-slate-600">
                                {r.duration_seconds ? `${r.duration_seconds}s` : '—'}
                              </td>
                              <td className="py-1 pr-2 tabular-nums text-slate-600">
                                {_fmtDate(r.stale_retry_flagged_at)}
                              </td>
                              <td className="py-1 pr-2 text-right">
                                <button
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); retrySingleCall(r.call_id); }}
                                  className="text-[10px] font-semibold text-white bg-indigo-600 hover:bg-indigo-500 px-2 py-0.5 rounded inline-flex items-center gap-1"
                                  data-testid={`stale-retry-row-${r.call_id}`}
                                  title="Re-queue this call for transcription + grading right now."
                                >
                                  <Repeat className="w-2.5 h-2.5" /> Retry
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </>
            ) : (
              <p className="text-xs italic text-slate-500">
                No sweeper data yet. Click &ldquo;Run sweeper now&rdquo; to seed the flags.
              </p>
            )}
          </section>

          {/* -- Slow requests -- */}
          <section className="border-t border-slate-100 pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Settings className="w-4 h-4 text-rose-600" />
              <p className="text-xs font-bold uppercase tracking-wider text-slate-700">Slow Request Log</p>
              <button
                onClick={loadSlow}
                className="ml-auto text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                data-testid="system-health-slow-refresh"
              >
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
              <button
                onClick={clearSlow}
                className="text-[11px] text-red-600 hover:text-red-800"
                data-testid="system-health-slow-clear"
              >
                Clear
              </button>
            </div>
            {loadingSlow ? (
              <div className="text-xs text-slate-500 py-2 inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading…
              </div>
            ) : slow.length === 0 ? (
              <p className="text-xs italic text-slate-500 py-2">
                No slow requests logged. The app is running fast.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="py-1 pr-2 font-semibold">When</th>
                      <th className="py-1 pr-2 font-semibold">Method</th>
                      <th className="py-1 pr-2 font-semibold">Path</th>
                      <th className="py-1 pr-2 font-semibold text-right">Status</th>
                      <th className="py-1 pr-2 font-semibold text-right">Elapsed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slow.map((r, i) => (
                      <tr key={`${r.ts}-${i}`} className="border-b border-slate-100">
                        <td className="py-1 pr-2 tabular-nums text-slate-600">{_fmtDate(r.ts)}</td>
                        <td className="py-1 pr-2 text-slate-700">{r.method}</td>
                        <td className="py-1 pr-2 truncate font-mono text-[11px]" title={r.path}>{r.path}</td>
                        <td className="py-1 pr-2 tabular-nums text-right text-slate-700">{r.status_code}</td>
                        <td className={`py-1 pr-2 tabular-nums text-right font-semibold ${
                          r.elapsed_ms > 5000 ? 'text-red-700' : r.elapsed_ms > 2500 ? 'text-amber-700' : 'text-slate-600'
                        }`}>{r.elapsed_ms}ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Extension Change Audit — folded into System Health per the
              Sep-2026 dashboard spec (was its own row on Intelligence). */}
          <section className="pt-2">
            <ExtensionReassignmentsPanel domainId={domainId} />
          </section>
        </div>
      )}
    </div>
  );
};

export default SystemHealthCard;
